<template>
  <div class="exam-prep-container">
    <div class="exam-prep-card" v-loading="loading">
      <template v-if="examInfo && !loading">
        <h1 class="exam-title">Assessment Exam</h1>
        <div class="exam-instructions">
          <p>You are about to begin the assessment exam for <strong>{{ collectionTitle }}</strong>.</p>
          <p v-if="examInfo.time_limit_enabled">
            The exam lasts <strong>{{ examInfo.time_limit_minutes }}</strong> minutes and contains
            <strong>{{ examInfo.question_count }}</strong> multiple-choice questions.
          </p>
          <p v-else>
            The exam contains <strong>{{ examInfo.question_count }}</strong> multiple-choice questions.
          </p>
          <p>
            The passing score is <strong>{{ examInfo.passing_score }}%</strong>.
            Your result will be displayed immediately after you finish the exam.
            If you pass, you will be awarded a course completion certificate.
          </p>
          <p>
            You have a total of <strong>{{ examInfo.max_attempts > 0 ? examInfo.max_attempts : 'unlimited' }}</strong> attempts.
            <template v-if="examInfo.cooldown_hours > 0">
              A minimum cooldown period of <strong>{{ examInfo.cooldown_hours }}</strong> hours is required between consecutive attempts.
            </template>
            This is your <strong>{{ getOrdinal(examInfo.attempts_used + 1) }}</strong> attempt.
          </p>
          <p v-if="examInfo.proctoring_enabled">
            The exam will be remotely proctored in the background through your device. Please enable the required permissions.
          </p>
          <p class="acknowledge-text">
            If you acknowledge and understand the above information, click the button below to start the exam.
          </p>
        </div>
        <div class="exam-actions">
          <el-button @click="goBack" size="medium">Go Back</el-button>
          <el-button type="primary" size="medium" @click="startExam" :loading="starting">
            Start Exam
          </el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { mapState } from 'vuex'

export default {
  name: 'ExamPrep',
  data() {
    return {
      loading: true,
      starting: false,
      examInfo: null,
      collectionTitle: ''
    }
  },
  computed: {
    ...mapState(['user_id']),
    collectionId() {
      return this.$route.query.collection_id
    }
  },
  mounted() {
    this.fetchExamInfo()
  },
  methods: {
    async fetchExamInfo() {
      this.loading = true
      try {
        const [infoRes, collRes] = await Promise.all([
          postRequest('getExamInfo', {
            collection_id: this.collectionId,
            user_id: this.user_id
          }),
          postRequest('getPostCollection', {
            id: this.collectionId,
            user_id: this.user_id
          })
        ])
        if (infoRes.data.code === 0) {
          this.examInfo = infoRes.data.data
        } else {
          this.$message.error(infoRes.data.msg || 'Failed to load exam info')
          this.goBack()
          return
        }
        if (collRes.data.code === 0) {
          this.collectionTitle = collRes.data.data?.title || 'Untitled'
        }
      } catch (error) {
        console.error('Error loading exam info:', error)
        this.$message.error('Failed to load exam information')
      } finally {
        this.loading = false
      }
    },
    async startExam() {
      this.starting = true
      try {
        const response = await postRequest('startExam', {
          collection_id: this.collectionId,
          user_id: this.user_id,
          device_info: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            screenWidth: screen.width,
            screenHeight: screen.height
          }
        })
        if (response.data.code === 0) {
          this.$router.replace({
            path: '/exam',
            query: {
              attempt_id: response.data.data.attempt_id,
              collection_id: this.collectionId
            }
          })
        } else {
          this.$message.error(response.data.msg || 'Failed to start exam')
        }
      } catch (error) {
        console.error('Error starting exam:', error)
        this.$message.error('Failed to start exam')
      } finally {
        this.starting = false
      }
    },
    goBack() {
      this.$router.push('/my-learning')
    },
    getOrdinal(n) {
      const s = ['th', 'st', 'nd', 'rd']
      const v = n % 100
      return n + (s[(v - 20) % 10] || s[v] || s[0])
    }
  }
}
</script>

<style scoped>
.exam-prep-container {
  min-height: 100vh;
  background: #f7f8fc;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 24px;
}

.exam-prep-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  max-width: 720px;
  width: 100%;
  padding: 48px;
  min-height: 300px;
}

.exam-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 32px;
  text-align: center;
}

.exam-instructions {
  font-size: 15px;
  line-height: 1.8;
  color: #303133;
}

.exam-instructions p {
  margin: 0 0 16px;
}

.acknowledge-text {
  margin-top: 24px;
  color: #606266;
  font-style: italic;
}

.exam-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 36px;
}

@media (max-width: 768px) {
  .exam-prep-container {
    padding: 24px 16px;
  }
  .exam-prep-card {
    padding: 28px 20px;
  }
  .exam-title {
    font-size: 20px;
  }
  .exam-instructions {
    font-size: 14px;
  }
}
</style>
